/**
 * Kubota — Smooth scroll + GSAP ScrollTrigger wiring.
 *
 * Initializes Lenis sitewide on desktop viewports and wires it into GSAP's
 * ticker so ScrollTrigger observes Lenis-driven scroll positions. All other
 * Kubota JS that needs to perform programmatic scrolling should use
 * `window.smoothScrollTo(target, opts)` so it falls back to native on mobile
 * or when reduced-motion is active.
 *
 * Public API:
 *   window.lenis            — the Lenis instance, or null when gated off
 *   window.smoothScrollTo   — scroll helper with native fallback
 *
 * Gates (ALL must pass for Lenis to initialize):
 *   1. Viewport >= 750px
 *   2. prefers-reduced-motion: reduce NOT matched
 *   3. Not inside Shopify theme editor (window.Shopify.designMode !== true)
 *
 * Spec: docs/superpowers/specs/2026-04-22-lenis-smooth-scroll-design.md
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 750;
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  var lenisInstance = null;
  var tickerCallback = null;

  function inThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode === true);
  }

  function shouldInitialize() {
    if (inThemeEditor()) return false;
    if (reducedMotionQuery.matches) return false;
    if (window.innerWidth < MOBILE_BREAKPOINT) return false;
    return true;
  }

  function initLenis() {
    if (lenisInstance) return;
    if (typeof Lenis === 'undefined' || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('[smooth-scroll] Lenis/GSAP/ScrollTrigger not loaded; skipping init.');
      return;
    }

    lenisInstance = new Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2
    });

    gsap.registerPlugin(ScrollTrigger);

    lenisInstance.on('scroll', ScrollTrigger.update);

    tickerCallback = function (time) {
      lenisInstance.raf(time * 1000);
    };
    gsap.ticker.add(tickerCallback);
    gsap.ticker.lagSmoothing(0);

    window.lenis = lenisInstance;
  }

  function destroyLenis() {
    if (!lenisInstance) return;
    if (tickerCallback && typeof gsap !== 'undefined') {
      gsap.ticker.remove(tickerCallback);
    }
    tickerCallback = null;
    lenisInstance.destroy();
    lenisInstance = null;
    window.lenis = null;
  }

  function getHeaderOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var parsed = parseFloat(raw);
    return isNaN(parsed) ? 0 : parsed;
  }

  function handleAnchorClick(event) {
    if (!window.lenis) return;
    var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || href === '#') return;
    var target;
    try {
      target = document.querySelector(href);
    } catch (e) {
      return; // invalid selector (e.g. `#!`)
    }
    if (!target) return;
    event.preventDefault();
    window.lenis.scrollTo(target, { offset: -getHeaderOffset() });
  }

  window.smoothScrollTo = function (target, opts) {
    opts = opts || {};
    if (window.lenis) return window.lenis.scrollTo(target, opts);
    var el = typeof target === 'string' ? document.querySelector(target) : target;
    if (el && typeof el.scrollIntoView === 'function') {
      var native = { behavior: 'smooth' };
      for (var k in opts) { if (Object.prototype.hasOwnProperty.call(opts, k)) native[k] = opts[k]; }
      el.scrollIntoView(native);
    }
  };

  var reconcileFrame = null;
  function reconcile() {
    if (reconcileFrame !== null) return;
    reconcileFrame = window.requestAnimationFrame(function () {
      reconcileFrame = null;
      if (shouldInitialize()) {
        initLenis();
      } else {
        destroyLenis();
      }
    });
  }

  function boot() {
    if (shouldInitialize()) initLenis();
    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('resize', reconcile);
    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', reconcile);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(reconcile); // Safari < 14 fallback
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
